// geometryUtils.js
// Utilidades geométricas para reparar mallas STL o realizar análisis espacial.
// Dependencias: three

import * as THREE from "three";

/**
 * Convierte una BufferGeometry de THREE.js a una lista de triángulos simples.
 * @param {THREE.BufferGeometry} geom
 * @returns {Array<{A:number[],B:number[],C:number[]}>}
 */
export function geomToTriangles(geom) {
  if (!geom) return [];
  const geo = geom.index ? geom.toNonIndexed() : geom.clone();
  const pos = geo.attributes.position.array;
  const tris = [];
  for (let i = 0; i < pos.length; i += 9) {
    tris.push({
      A: [pos[i], pos[i + 1], pos[i + 2]],
      B: [pos[i + 3], pos[i + 4], pos[i + 5]],
      C: [pos[i + 6], pos[i + 7], pos[i + 8]],
    });
  }
  return tris;
}

/**
 * Calcula el bounding box global (min y max en 3D) de una lista de triángulos.
 * @param {Array<{A:number[],B:number[],C:number[]}>} tris
 * @returns {{min:number[],max:number[]}}
 */
export function computeBoundingBox(tris) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const t of tris) {
    for (const p of [t.A, t.B, t.C]) {
      for (let i = 0; i < 3; i++) {
        if (p[i] < min[i]) min[i] = p[i];
        if (p[i] > max[i]) max[i] = p[i];
      }
    }
  }
  return { min, max };
}

/**
 * Producto punto de dos vectores 3D.
 */
export function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * Producto cruz de dos vectores 3D.
 */
export function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/**
 * Distancia punto–triángulo en 3D (métrica Euclidiana).
 * @param {number[]} p Punto [x,y,z]
 * @param {number[]} a Vértice A del triángulo
 * @param {number[]} b Vértice B del triángulo
 * @param {number[]} c Vértice C del triángulo
 * @returns {number} Distancia mínima
 */
export function pointTriangleDistance(p, a, b, c) {
  const px = p[0], py = p[1], pz = p[2];
  const ax = a[0], ay = a[1], az = a[2];
  const bx = b[0], by = b[1], bz = b[2];
  const cx = c[0], cy = c[1], cz = c[2];

  const ABx = bx - ax, ABy = by - ay, ABz = bz - az;
  const ACx = cx - ax, ACy = cy - ay, ACz = cz - az;
  const APx = px - ax, APy = py - ay, APz = pz - az;

  const d1 = ABx * APx + ABy * APy + ABz * APz;
  const d2 = ACx * APx + ACy * APy + ACz * APz;
  if (d1 <= 0 && d2 <= 0) return Math.hypot(APx, APy, APz); // A

  const BPx = px - bx, BPy = py - by, BPz = pz - bz;
  const d3 = ABx * BPx + ABy * BPy + ABz * BPz;
  const d4 = ACx * BPx + ACy * BPy + ACz * BPz;
  if (d3 >= 0 && d4 <= d3) return Math.hypot(BPx, BPy, BPz); // B

  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3);
    const proj = [ax + v * ABx, ay + v * ABy, az + v * ABz];
    return Math.hypot(px - proj[0], py - proj[1], pz - proj[2]);
  }

  const CPx = px - cx, CPy = py - cy, CPz = pz - cz;
  const d5 = ABx * CPx + ABy * CPy + ABz * CPz;
  const d6 = ACx * CPx + ACy * CPy + ACz * CPz;
  if (d6 >= 0 && d5 <= d6) return Math.hypot(CPx, CPy, CPz); // C

  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6);
    const proj = [ax + w * ACx, ay + w * ACy, az + w * ACz];
    return Math.hypot(px - proj[0], py - proj[1], pz - proj[2]);
  }

  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && (d4 - d3) >= 0 && (d5 - d6) >= 0) {
    const w = (d4 - d3) / ((d4 - d3) + (d5 - d6));
    const proj = [bx + w * (cx - bx), by + w * (cy - by), bz + w * (cz - bz)];
    return Math.hypot(px - proj[0], py - proj[1], pz - proj[2]);
  }

  // Región interna
  const nx = ABy * ACz - ABz * ACy;
  const ny = ABz * ACx - ABx * ACz;
  const nz = ABx * ACy - ABy * ACx;
  const len = Math.hypot(nx, ny, nz) || 1e-12;
  const nux = nx / len, nuy = ny / len, nuz = nz / len;
  const projDist = Math.abs(APx * nux + APy * nuy + APz * nuz);
  return projDist;
}

/**
 * Intersección rayo–triángulo (Möller–Trumbore)
 * @param {number[]} orig Origen del rayo [x,y,z]
 * @param {number[]} dir Dirección normalizada [x,y,z]
 * @param {number[]} a Vértice A
 * @param {number[]} b Vértice B
 * @param {number[]} c Vértice C
 * @returns {boolean} true si hay intersección
 */
export function rayIntersectsTriangle(orig, dir, a, b, c) {
  const EPS = 1e-9;
  const edge1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const edge2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const pvec = cross(dir, edge2);
  const det = dot(edge1, pvec);
  if (det > -EPS && det < EPS) return false; // paralelos
  const invDet = 1.0 / det;
  const tvec = [orig[0] - a[0], orig[1] - a[1], orig[2] - a[2]];
  const u = dot(tvec, pvec) * invDet;
  if (u < 0 || u > 1) return false;
  const qvec = cross(tvec, edge1);
  const v = dot(dir, qvec) * invDet;
  if (v < 0 || u + v > 1) return false;
  const t = dot(edge2, qvec) * invDet;
  return t > EPS;
}

export default {
  geomToTriangles,
  computeBoundingBox,
  pointTriangleDistance,
  rayIntersectsTriangle,
  dot,
  cross,
};
